import React, { useEffect, useState } from "react";
import { TagsInput } from "react-tag-input-component";
import { useFormik } from "formik";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { UPDATE_STORE_TIP_SETTINGS } from "src/constants/mutations";
import { GET_STORE_TIP_SETTINGS } from "src/constants/queries";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { successToast, failureToast } from "src/utils/toasts";
import { BtnLoader } from "src/Loader";
import FormRowVertical from "src/components/common/FormRowVertical";
import { StoreTipSettings, UpdateStoreTipSettingsInput } from "src/interfaces/tipSettings.interface";

const SuggestedTips = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant, sethasRestaurant } = useAuth();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      suggestedTipPercentages: [] as string[],
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!hasRestaurant?.correlationId) return;

    const fetchTipSettings = async () => {
      try {
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_STORE_TIP_SETTINGS,
          variables: {
            retrieveStoresInput: {
              storeCorrelationIds: [hasRestaurant.correlationId],
            },
          },
        });

        if (data?.errors?.length) {
          throw data.errors;
        }

        const stores = data?.data?.stores || [];
        const storeData: StoreTipSettings | null = stores.length > 0 ? stores[0] : null;
        if (storeData?.suggestedTipPercentages?.length) {
          const tips = storeData.suggestedTipPercentages.map(
            (t: { tipPercentage: string }) => t.tipPercentage ?? String(t)
          );
          formik.setFieldValue("suggestedTipPercentages", tips);
        }
      } catch (error) {
        console.error("Error fetching suggested tips:", error);
        const err = error as { message?: string; [0]?: { message?: string } };
        failureToast(err?.message || err?.[0]?.message || "Failed to load suggested tips");
      }
    };

    fetchTipSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when restaurant changes; formik.setFieldValue is stable
  }, [hasRestaurant?.correlationId]);

  const updateRestaurantSetting = async () => {
    setLoading(true);
    try {
      const formattedTips = formik.values.suggestedTipPercentages.map((tip) => {
        const strValue = String(tip);
        return strValue.replace("%", "");
      });

      const input: UpdateStoreTipSettingsInput = {
        storeCorrelationId: hasRestaurant.correlationId,
        suggestedTipPercentages: formattedTips,
      };

      const { data }: { data?: { errors?: unknown[] } } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_STORE_TIP_SETTINGS,
        variables: { updateStoreTipSettingsInput: input },
      });

      if (data?.errors?.length) {
        throw data.errors;
      }

      const payloadData = {
        ...hasRestaurant,
        suggestedTipPercentages: formattedTips,
      };
      localStorage.setItem("restaurant", JSON.stringify(payloadData));
      sethasRestaurant(payloadData);
      successToast("Suggested Tip Percentages have been Updated!");
    } catch (error) {
      console.error("Error updating suggested tips:", error);
      const err = error as { message?: string; [0]?: { message?: string } };
      failureToast(err?.message || err?.[0]?.message || "Failed to save suggested tips");
    } finally {
      setLoading(false);
    }
  };

  const handleTagsInputChange = (value: string[]) => {
    const validValues = value
      .map((v) => {
        const strValue = String(v);
        const numericValue = strValue.replace("%", "");
        if (/^\d+(\.\d{1,2})?$/.test(numericValue)) {
          return numericValue;
        }
        return "";
      })
      .filter((v) => v !== "");
    formik.setFieldValue("suggestedTipPercentages", validValues);
  };

  const isDisabled = !formik.isValid || !formik.dirty;

  return (
    <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
      <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-2">Suggested Tip Ratios</h2>
      <p className="text-[14px] text-[#6b7280] mb-6">
        Input tip ratios as decimals between 0 and 1 (e.g. 0.10, 0.15, 0.20), and press <span className="suggested-tips-enter-key">ENTER</span>.
      </p>

      <div className="space-y-4">
        <FormRowVertical label="Tip percentages">
          <TagsInput
            value={formik.values.suggestedTipPercentages}
            name="suggestedTipPercentages"
            placeHolder="Enter Tips"
            onChange={handleTagsInputChange}
            beforeAddValidate={(tag) => +tag >= 0 && +tag <= 1}
          />
        </FormRowVertical>

        <div className="flex justify-end mt-6">
          {loading ? (
            <BtnLoader />
          ) : (
            <button
              type="button"
              disabled={isDisabled}
              onClick={updateRestaurantSetting}
              className={`px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuggestedTips;
